import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { config } from '@/core/config'
import { useAuthStore } from '@/store/auth.store'
import type {
  AddCombinedAttendanceRequest,
  AddCombinedAttendancePayload,
  AddAttendancePayload,
  AttendanceListRecord,
  AttendanceRecord,
  AttendanceUpdateRequest,
} from '@/api/types'

export class AttendanceService {
  /** List: GET /api/Attendance/GetAttendance/{branchId}/{pageNumber}/{pageSize} */
  async getList(
    branchId: number,
    pageNumber = 1,
    pageSize = 50
  ): Promise<{ data: AttendanceListRecord[]; totalRecords: number }> {
    const url = endpoints.attendance.getList(branchId, pageNumber, pageSize)
    const res = await apiClient.get<{ data: AttendanceListRecord[]; totalRecords: number; pageNumber?: number; pageSize?: number }>(url)
    const raw = res.data
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) {
      return {
        data: (raw as any).data,
        totalRecords: Number((raw as any).totalRecords) || 0,
      }
    }
    return { data: [], totalRecords: 0 }
  }

  /** List by filters: GET /api/Attendance/GetAttendanceByDepartmentEmployeeDateRange */
  async getListByFilters(
    branchId: number,
    params: {
      departmentId?: number
      empId?: number
      dateFrom: string
      dateTo: string
      pageNumber?: number
      pageSize?: number
    }
  ): Promise<{ data: AttendanceListRecord[]; totalRecords: number }> {
    const {
      departmentId = 0,
      empId = 0,
      dateFrom,
      dateTo,
      pageNumber = 1,
      pageSize = 50,
    } = params
    const url = endpoints.attendance.getByDepartmentEmployeeDateRange(
      departmentId,
      empId,
      dateFrom,
      dateTo,
      pageNumber,
      pageSize,
      branchId
    )
    const res = await apiClient.get<{ data: AttendanceListRecord[]; totalRecords: number; pageNumber?: number; pageSize?: number }>(url)
    const raw = res.data
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) {
      return {
        data: (raw as any).data,
        totalRecords: Number((raw as any).totalRecords) || 0,
      }
    }
    if (Array.isArray(raw)) {
      return { data: raw as AttendanceListRecord[], totalRecords: (raw as AttendanceListRecord[]).length }
    }
    return { data: [], totalRecords: 0 }
  }

  async addCombinedAttendance(payload: AddCombinedAttendanceRequest): Promise<{ result?: { isError?: boolean; msg?: string } }> {
    const res = await apiClient.post(endpoints.attendance.addCombined, payload)
    return res.data
  }

  /** POST /api/Attendance/AddCombinedAttendanceAsync with departments[], employeeIds[], date range, times, etc. */
  async addCombinedAttendanceBatch(payload: AddCombinedAttendancePayload): Promise<{ result?: { isError?: boolean; msg?: string } }> {
    const res = await apiClient.post(endpoints.attendance.addCombined, payload)
    return res.data
  }

  /** POST /api/Attendance/AddAttendanceAsync - single attendance record */
  async addAttendance(payload: AddAttendancePayload): Promise<unknown> {
    const res = await apiClient.post(endpoints.attendance.addSingle, payload)
    return res.data
  }

  async getByMonthYear(
    monthYear: string,
    branchId: number,
    pageNumber = 1,
    pageSize = 50
  ): Promise<{ data: AttendanceRecord[]; totalRecords: number }> {
    const mmYyyy = String(monthYear).replace(/\s/g, '-').trim()
    const path = endpoints.attendance.getByMonthYear(mmYyyy, branchId, pageNumber, pageSize)
    const base = (config.api.baseUrl || '').replace(/\/$/, '')
    const fullUrl = `${base}${path.startsWith('/') ? path : '/' + path}`
    const hasAuth = !!useAuthStore.getState().token
    console.log('[Attendance GET] Params:', { monthYear: mmYyyy, branchId, pageNumber, pageSize })
    console.log('[Attendance GET] Full URL:', fullUrl)
    console.log('[Attendance GET] Authorization header:', hasAuth ? 'sent' : 'MISSING (backend may return 404)')
    try {
      const res = await apiClient.get<
        AttendanceRecord[] | { data: AttendanceRecord[]; totalRecords?: number; pageNumber?: number; pageSize?: number }
      >(fullUrl)
      const raw = res.data
      console.log('[Attendance GET] API response (success):', raw)
      let arr: AttendanceRecord[] = []
      let total = 0
      if (Array.isArray(raw)) {
        arr = raw
        total = raw.length
      } else if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) {
        arr = (raw as any).data
        total = Number((raw as any).totalRecords) || arr.length
      }
      if (arr.length > 0) {
        const sample = arr[0] as any
        console.log('[Attendance GET] First record (API fields):', {
          id: sample.id,
          empId: sample.empId,
          timeIn: sample.timeIn,
          timeOut: sample.timeOut,
          attendanceDate: sample.attendanceDate,
          shiftId: sample.shiftId,
          isPresent: sample.isPresent,
          isPaidLeave: sample.isPaidLeave,
          isShortLeave: sample.isShortLeave,
          isWorkFromHome: sample.isWorkFromHome,
          totalAttendedHours: sample.totalAttendedHours,
          attendanceLocationId: sample.attendanceLocationId,
          machineId: sample.machineId,
          description: sample.description,
          createdById: sample.createdById,
          createdDate: sample.createdDate,
          updatedById: sample.updatedById,
          updatedDate: sample.updatedDate,
          departmentId: sample.departmentId,
          attendanceMonth: sample.attendanceMonth,
          attendanceStatus: sample.attendanceStatus,
          lateHoursCount: sample.lateHoursCount,
          isFlexible: sample.isFlexible,
          isWeekend: sample.isWeekend,
          isHoliday: sample.isHoliday,
          isUpdated: sample.isUpdated,
          isLate: sample.isLate,
          isPostedPayroll: sample.isPostedPayroll,
          isPosted: sample.isPosted,
          uncompleteHours: sample.uncompleteHours,
          branchId: sample.branchId,
        })
      }
      return { data: arr, totalRecords: total }
    } catch (e: any) {
      const status = e?.response?.status
      const responseData = e?.response?.data
      console.log('[Attendance GET] API response (error):', { status, data: responseData })
      throw e
    }
  }

  async update(payload: AttendanceUpdateRequest): Promise<unknown> {
    const res = await apiClient.put(endpoints.attendance.update, payload)
    return res.data
  }

  /** PUT /api/Attendance/PostAttendanceAsync?dateFrom=&dateTo=&departmentId=&branchId= */
  async postAttendance(
    dateFrom: string,
    dateTo: string,
    departmentId: number,
    branchId: number
  ): Promise<unknown> {
    const url = endpoints.attendance.post(dateFrom, dateTo, departmentId, branchId)
    const res = await apiClient.put(url)
    return res.data
  }
}

export const attendanceService = new AttendanceService()
